import { useEffect, useState } from "react";
import axios from "axios";

// Single source of truth for /api/schools/my-settings — fetched once per page
// load and shared by every consumer (currency, feature flags, print letterhead).
let cached = null;
let pending = null;
// Live subscribers, so a save in Settings updates settings-derived chrome (the
// sidebar logo, currency symbols) without waiting for a page reload.
const listeners = new Set();

const DEFAULTS = {
  currency: "",
  logo: "",
  termsEnabled: false,
  feeMode: "monthly",
  timingMode: "per_schedule",
  timingConfig: null,
};

export const getSchoolSettings = async () => {
  if (cached) return cached;
  if (!pending) {
    pending = axios
      .get("/api/schools/my-settings")
      .then((res) => {
        cached = { ...DEFAULTS, ...(res.data?.data || {}) };
        return cached;
      })
      .catch(() => {
        cached = { ...DEFAULTS };
        return cached;
      });
  }
  return pending;
};

/** Call after saving settings so the next read picks up the change. */
export const invalidateSchoolSettings = () => {
  cached = null;
  pending = null;
  getSchoolSettings().then((next) => listeners.forEach((fn) => fn(next)));
};

/** React hook: returns school settings, defaults until loaded. */
export const useSchoolSettings = () => {
  const [settings, setSettings] = useState(cached || DEFAULTS);
  useEffect(() => {
    let alive = true;
    getSchoolSettings().then((s) => alive && setSettings(s));
    listeners.add(setSettings);
    return () => {
      alive = false;
      listeners.delete(setSettings);
    };
  }, []);
  return settings;
};

/**
 * React hook for the optional-feature flags.
 * Schools that never opt in get { termsEnabled: false, feeMode: "monthly" },
 * which is the pre-existing behaviour everywhere.
 */
export const useSchoolFeatures = () => {
  const { termsEnabled, feeMode, timingMode, timingConfig } = useSchoolSettings();
  return {
    termsEnabled: termsEnabled === true,
    feeMode: feeMode === "weekly_per_term" ? "weekly_per_term" : "monthly",
    // Convenience: terms must be on for weekly billing to apply.
    weeklyBilling: termsEnabled === true && feeMode === "weekly_per_term",
    // How class times are decided across the app's schedule views.
    timingMode: ["per_schedule", "seasonal", "per_term"].includes(timingMode)
      ? timingMode
      : "per_schedule",
    timingConfig: timingConfig || null,
  };
};
